import { motion } from 'framer-motion'
import {
  Camera,
  Check,
  KeyRound,
  Languages,
  LogOut,
  Mail,
  ShieldCheck,
  Trash2,
} from 'lucide-react'
import { useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { LanguageSelect } from '@/components/ui/LanguageSelect'
import { RoleName } from '@/components/RoleName'
import { StatusPill } from '@/components/ui/StatusPill'
import {
  AVATAR_ACCEPT,
  AvatarRejectedError,
  useRemoveAvatar,
  useUploadAvatar,
} from '@/features/auth/api/useAvatar'
import { useSession } from '@/features/auth/api/useSession'
import { useSignOut } from '@/features/auth/api/useSignOut'
import { useUpdateLanguage } from '@/features/auth/api/useUpdateLanguage'
import { cn } from '@/lib/cn'
import { staggerChildVariants, staggerParentVariants } from '@/lib/motion'
import type { CurrentUser } from '@/types/domain'

/* The user's own account, reached from the profile block in persistent chrome.
 *
 * This page is deliberately NOT a settings screen. Roles (FR-42) and ownership
 * grants (FR-44) are set on the user record in Directories by a Super-admin —
 * they are shown here as facts, read-only, because the person reading this page
 * is the one person who may not change them. Saying where they ARE changed
 * costs one line and prevents the WhatsApp message to a founder.
 *
 * The language switch (FR-58) is genuinely the user's own, so it writes.
 * Russian is a first-class UI language, not a translation layer over an English
 * product.
 *
 * The profile photo is the other. It is the user's own in the same sense —
 * nobody else sets it, and the endpoints behind it take their subject from the
 * session rather than from a URL — which is why it lives on this page and not
 * in the user record a Super-admin edits in Directories.
 *
 * The identity card at the top is the one surface in this product that is
 * allowed to be a portrait rather than a readout. Everywhere else the job is
 * density — rows of money to be scanned. Here there is exactly one record, it
 * is the reader's own, and it is reached deliberately from the topbar, so the
 * space goes to the photo and the name instead of to a fourth column. That is
 * also why it is the only card carrying a tinted wash: it is the page's subject,
 * not one panel among equals. The wash is brand-soft at partial strength, so
 * Gateway Blue stays the loudest thing on screen.
 */

/** Grants are additive booleans on the user, never new roles (AD-7). The
 *  catalogue key is derived from the grant name at render. */
const GRANT_KEYS = [
  'manageClients',
  'manageParticipants',
  'manageTransport',
] as const satisfies readonly (keyof CurrentUser['grants'])[]

export function ProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: user, isPending, isError, error, refetch } = useSession()
  const updateLanguage = useUpdateLanguage()
  const signOut = useSignOut()

  const uploadAvatar = useUploadAvatar()
  const removeAvatar = useRemoveAvatar()
  // The file input is visually hidden and driven by a real Button, so the
  // control matches every other button on the page instead of rendering as the
  // browser's unstyleable default.
  const pickerRef = useRef<HTMLInputElement>(null)

  // Reset the sibling mutation on each attempt, so a failed upload's banner
  // does not sit above a successful removal (and the reverse).
  function pickPhoto(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    removeAvatar.reset()
    uploadAvatar.mutate(file)
  }

  if (isPending) {
    return (
      <>
        <PageHeader title={t('profile.title')} />
        {/* The skeleton mirrors the real layout's proportions — a tall hero
            over two equal cards — so the page does not visibly re-flow when
            the data lands. */}
        <div className="flex flex-col gap-3.5" aria-busy="true" aria-label={t('profile.loading')}>
          <Skeleton className="h-[168px] rounded-[var(--radius-DEFAULT)]" />
          <div className="grid gap-3.5 lg:grid-cols-2">
            <Skeleton className="h-56 rounded-[var(--radius-DEFAULT)]" />
            <Skeleton className="h-56 rounded-[var(--radius-DEFAULT)]" />
          </div>
        </div>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <PageHeader title={t('profile.title')} />
        <ErrorState error={error} onRetry={refetch} />
      </>
    )
  }

  const grants = GRANT_KEYS.filter((key) => user.grants[key])

  /* The one line under the name that reports what just happened. Errors name
     the REASON, because "that did not work" leaves a user retrying the same
     12 MB photo. Resolved here rather than in the JSX so the four states read
     as the one slot they are, instead of as a three-deep ternary buried in the
     middle of the card. */
  const photoStatus = uploadAvatar.isPending
    ? { alert: false, tone: 'text-text-muted', text: t('profile.photo.uploading') }
    : uploadAvatar.isError
      ? {
          alert: true,
          tone: 'text-negative',
          text:
            uploadAvatar.error instanceof AvatarRejectedError
              ? t(`profile.photo.rejected.${uploadAvatar.error.reason}`)
              : t('profile.photo.failed'),
        }
      : removeAvatar.isError
        ? { alert: true, tone: 'text-negative', text: t('profile.photo.removeFailed') }
        : { alert: false, tone: 'text-text-faint', text: t('profile.photo.hint') }

  return (
    <>
      <PageHeader title={t('profile.title')} subtitle={t('profile.subtitle')} />

      {/* The whole page is one arrival: the parent times its children, and each
          card fades up 6px just behind the one above it. Cards opt in by
          carrying the child variants — nothing here computes a delay. */}
      <motion.div
        variants={staggerParentVariants}
        initial="hidden"
        animate="visible"
        className="flex flex-col gap-3.5"
      >
        {/* ── Identity ──────────────────────────────────────────────────────
            The avatar and name repeat what the topbar already shows —
            deliberately: this page has to confirm WHOSE account it is before
            it states what that account may do. */}
        <motion.div variants={staggerChildVariants}>
          <Card className="relative overflow-hidden">
            {/* The wash. A single soft brand field bled from the top-left,
                masked to nothing well before the text starts, so the card
                reads as lit rather than as a coloured box. aria-hidden and
                pointer-events-none: it is paint, and it must never intercept
                a click meant for the photo button beneath it. */}
            <div
              aria-hidden
              className={cn(
                'pointer-events-none absolute inset-0',
                'bg-[radial-gradient(120%_140%_at_0%_0%,var(--color-brand-soft)_0%,transparent_58%)]',
                'opacity-70',
              )}
            />

            <CardBody className="relative flex flex-wrap items-center gap-x-5 gap-y-4 py-5">
              {/* The photo, and the control that sets it. Grouped so the whole
                  thing is one target: clicking the picture is what people try
                  first, and a camera badge that only decorates would be a lie. */}
              <motion.button
                type="button"
                onClick={() => pickerRef.current?.click()}
                disabled={uploadAvatar.isPending}
                aria-label={t('profile.photo.change')}
                /* The one press-feedback in the system. It is here because
                   this is a photo the user is replacing — a control whose
                   result is a picture earns a physical acknowledgement that a
                   Save button does not. 0.97 and 80ms: felt, not watched, and
                   it respects prefers-reduced-motion through Framer's own
                   reduced-motion handling. */
                whileTap={uploadAvatar.isPending ? undefined : { scale: 0.97 }}
                transition={{ duration: 0.08 }}
                className={cn(
                  'group relative shrink-0 rounded-full',
                  'focus-visible:outline-brand focus-visible:outline-2 focus-visible:outline-offset-2',
                  uploadAvatar.isPending ? 'cursor-wait' : 'cursor-pointer',
                )}
              >
                {/* A ring in the page background colour separates the photo
                    from the wash behind it without drawing a border — the
                    system has no shadow to lean on here. */}
                <Avatar
                  name={user.name}
                  src={user.avatarUrl}
                  size="lg"
                  className={cn(
                    'ring-surface size-[72px] text-[24px] font-light ring-4',
                    'transition-opacity duration-[var(--dur-fast)]',
                    uploadAvatar.isPending && 'opacity-55',
                  )}
                />

                {/* The affordance. Always visible rather than hover-only: this
                    page is reached from a phone as often as a desktop, and a
                    hover state is not a thing a touch screen has. It fills
                    with brand under the pointer, which is the whole hover
                    state — the card itself does not move. */}
                <span
                  aria-hidden
                  className={cn(
                    'bg-surface border-line absolute right-0 bottom-0',
                    'flex size-[26px] items-center justify-center rounded-full border',
                    'text-text-muted transition-colors duration-[var(--dur-fast)]',
                    'group-hover:bg-brand group-hover:border-brand group-hover:text-text-on-brand',
                  )}
                >
                  <Camera size={13} />
                </span>
              </motion.button>

              {/* Visually hidden rather than `display:none`, so it stays
                  reachable by assistive technology and by the button standing
                  in for it. `accept` is derived from the same list the client
                  and server both validate against, so the picker cannot offer
                  a type that is then refused. */}
              <input
                ref={pickerRef}
                type="file"
                accept={AVATAR_ACCEPT}
                className="sr-only"
                aria-label={t('profile.photo.change')}
                onChange={(event) => {
                  pickPhoto(event.target.files)
                  // Cleared so choosing the SAME file again still fires a
                  // change event — otherwise a retry after a failed upload
                  // does nothing.
                  event.target.value = ''
                }}
              />

              <div className="flex min-w-0 flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                  {/* The largest thing on the page, because it is the page's
                      subject. Display size, Regular — headings are never
                      bold here. */}
                  <h2 className="text-text-heading text-[26px] leading-none font-normal tracking-[-0.7px]">
                    {user.name}
                  </h2>
                  <StatusPill tone="brand"><RoleName role={user.role} /></StatusPill>
                </div>

                <a
                  href={`mailto:${user.email}`}
                  className={cn(
                    'text-text-muted hover:text-text-link flex w-fit items-center gap-1.5',
                    'text-[13px] font-light no-underline transition-colors',
                    'duration-[var(--dur-fast)] hover:underline hover:underline-offset-2',
                  )}
                >
                  <Mail size={14} aria-hidden />
                  {user.email}
                </a>

                <span
                  role={photoStatus.alert ? 'alert' : undefined}
                  className={cn(
                    'text-[12.5px]',
                    photoStatus.alert ? 'font-normal' : 'font-light',
                    photoStatus.tone,
                  )}
                >
                  {photoStatus.text}
                </span>
              </div>

              {/* Both photo controls and the exit, pushed to the far end. On a
                  phone this wraps to its own line rather than crushing the
                  name — which is why the gap above is split x/y. */}
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {/* Removal is a quieter control, and only when there is
                    something to remove. It is not `danger`: a photo is
                    replaceable and the red is reserved for destructive and
                    financial acts. */}
                {user.avatarUrl ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={Trash2}
                    disabled={removeAvatar.isPending || uploadAvatar.isPending}
                    onClick={() => {
                      uploadAvatar.reset()
                      removeAvatar.mutate()
                    }}
                  >
                    {t('profile.photo.remove')}
                  </Button>
                ) : null}

                {/* The voluntary password change (QW-5). The page lives
                    outside the shell because a FLAGGED account must reach it
                    with nothing else mounted; an unflagged one gets there from
                    here and has a Cancel back. */}
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={KeyRound}
                  onClick={() => navigate('/change-password')}
                >
                  {t('profile.changePassword')}
                </Button>

                {/* Sign-out lives here rather than in the topbar: this is the
                    account surface, and a one-click exit in persistent chrome
                    is a misclick waiting to happen on a dense operational
                    screen. It is `secondary`, not `danger` — signing out
                    destroys nothing, and the red reserved for destructive and
                    financial acts would overstate it (Story 1.2). */}
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={LogOut}
                  disabled={signOut.isPending}
                  onClick={() =>
                    signOut.mutate(undefined, {
                      /* The guard would redirect on its own once the cache
                         clears, but naming the destination keeps the exit
                         deliberate rather than leaving it to a re-render, and
                         `replace` keeps the signed-in page out of history. */
                      onSettled: () => navigate('/login', { replace: true }),
                    })
                  }
                >
                  {t('auth.signOut')}
                </Button>
              </div>
            </CardBody>
          </Card>
        </motion.div>

        {/* The two halves of the account: what you may change, and what you
            may not. They are siblings on a grid so the distinction is spatial
            rather than something the copy has to carry alone. */}
        <div className="grid gap-3.5 lg:grid-cols-2">
          {/* ── Language — the one control on this page that writes. ── */}
          <motion.div variants={staggerChildVariants} className="flex">
            <Card className="flex-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Languages size={15} aria-hidden className="text-text-muted shrink-0" />
                  {t('profile.language.title')}
                </CardTitle>
                <CardSubtitle>{t('profile.language.description')}</CardSubtitle>
              </CardHeader>
              <CardBody>
                <LanguageSelect
                  variant="full"
                  value={user.language}
                  onChange={(language) => updateLanguage.mutate(language)}
                  className="max-w-[280px]"
                />
                {updateLanguage.isError ? (
                  <p role="alert" className="text-negative mt-2.5 text-[12.5px] font-normal">
                    {t('profile.language.error')}
                  </p>
                ) : null}
              </CardBody>
            </Card>
          </motion.div>

          {/* ── Access — read-only by design, and it says who to ask. ── */}
          <motion.div variants={staggerChildVariants} className="flex">
            <Card className="flex-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck size={15} aria-hidden className="text-text-muted shrink-0" />
                  {t('profile.access.title')}
                </CardTitle>
                <CardSubtitle>
                  {/* The sentence wraps a link, and Russian puts it in a
                      different place, so the link is a POSITIONAL slot in the
                      string rather than a concatenation. */}
                  <Trans
                    i18nKey="profile.access.description"
                    components={[<Link key="directories" to="/directories" />]}
                  />
                </CardSubtitle>
              </CardHeader>
              <CardBody className="flex flex-col gap-2.5">
                {/* The role, stated as the headline fact of this card rather
                    than as another list item — the grants below MODIFY it, so
                    they cannot sit at the same weight. */}
                <div
                  className={cn(
                    'bg-surface-sunken border-line-hair flex items-center gap-2.5',
                    'rounded-[var(--radius-md)] border px-3 py-2.5',
                  )}
                >
                  <span className="bg-brand-soft text-brand flex size-7 shrink-0 items-center justify-center rounded-full">
                    <ShieldCheck size={15} aria-hidden />
                  </span>
                  <span className="text-text-heading text-[13px] font-normal">
                    <RoleName role={user.role} />
                  </span>
                </div>

                {grants.length > 0 ? (
                  <ul className="flex flex-col gap-1">
                    {grants.map((grant) => (
                      <li
                        key={grant}
                        className={cn(
                          'flex items-center gap-2.5 rounded-[var(--radius-sm)] px-1 py-1.5',
                          'transition-colors duration-[var(--dur-fast)] hover:bg-surface-hover',
                        )}
                      >
                        <Check size={16} aria-hidden className="text-positive shrink-0" />
                        <span className="text-text-body text-[13px] font-normal">
                          {t(`profile.access.grants.${grant}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  // Where nothing is wrong, say so plainly. Never apologise.
                  <p className="text-text-muted px-1 text-[12.5px] leading-snug font-light">
                    {t('profile.access.noGrants')}
                  </p>
                )}
              </CardBody>
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </>
  )
}
